import { dateLocale } from "@/lib/dateTime";
import { closeAllPopups, closePopupsFor, setBadge } from "@/lib/push";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bell, CheckCheck, ChevronDown, Inbox, Menu, PanelLeft, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import type { NotificationItem } from "@/types/notification";
import { useCreateActions, usePalette } from "./CommandPalette";
import { getCrumbs } from "./routeMeta";
import { useShell } from "./ShellContext";
import { RecentMenu } from "@/components/recent/RecentMenu";
import { CallChip } from "@/components/calling/CallChip";
import { ContactedChip } from "./ContactedChip";
import { AiIcon } from "@/components/ai/AiIcon";
import { useAskPanel } from "@/components/ai/AskPanelContext";
import { cn } from "@/lib/utils";

function timeAgo(dateStr: string) {
  const min = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min}m ago`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour}h ago`;
  if (hour < 48) return "Yesterday";
  return new Date(dateStr).toLocaleDateString(dateLocale(), { month: "short", day: "numeric" });
}

function Notifications() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [open, setOpen] = useState(false);

  const loadCount = () =>
    api
      .get("/api/Notification/UnreadCount")
      .then((res) => setUnread(unwrapObject<{ count: number }>(res).count || 0))
      .catch(() => undefined);

  // The number on the app's icon follows the bell
  useEffect(() => setBadge(unread), [unread]);

  // Poll every minute and on every page change
  useEffect(() => {
    loadCount();
    const timer = setInterval(loadCount, 60000);
    // A push just arrived (or was tapped): don't wait for the next minute
    const now = () => void loadCount();
    window.addEventListener("crm:notification", now);
    window.addEventListener("crm:unread-changed", now);
    return () => {
      clearInterval(timer);
      window.removeEventListener("crm:notification", now);
      window.removeEventListener("crm:unread-changed", now);
    };
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    api
      .get("/api/Notification/List?limit=15")
      .then((res) => setItems(unwrapArray<NotificationItem>(res)))
      .catch(() => undefined);
  }, [open]);

  const markAll = async () => {
    try {
      await api.patch("/api/Notification/MarkAllRead");
      void closeAllPopups();
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read_at: new Date().toISOString() })));
    } catch {
      /* the list refreshes next time it opens */
    }
  };

  const openItem = async (n: NotificationItem) => {
    if (!n.read_at) {
      try {
        await api.patch(`/api/Notification/MarkRead/${n.id}`);
        void closePopupsFor([n]);
        setUnread((c) => Math.max(0, c - 1));
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
      } catch {
        /* still navigate */
      }
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon-sm" aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"} className="relative">
          <Bell className="size-4" />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-sidebar">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[450px] w-80 overflow-y-auto p-0">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-popover px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notifications{unread > 0 && ` (${unread})`}</span>
          {unread > 0 && (
            <button type="button" onClick={markAll} className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
              <CheckCheck className="size-3.5" />
              Mark all read
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <Inbox className="mx-auto mb-1.5 size-8 opacity-40" />
            <p className="text-xs">No notifications yet</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => openItem(n)}
                  className={`flex w-full items-start gap-2.5 p-3 text-left transition-colors hover:bg-muted/60 ${!n.read_at ? "bg-primary/5" : ""}`}
                >
                  {!n.read_at && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />}
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className={`truncate text-xs ${!n.read_at ? "font-semibold" : "font-medium text-foreground/80"}`}>{n.title}</span>
                      <span className="shrink-0 text-[10px] text-muted-foreground">{timeAgo(n.created_on)}</span>
                    </span>
                    <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{n.body}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="sticky bottom-0 border-t border-border bg-popover px-4 py-2.5 text-center">
          <Link to="/notifications" onClick={() => setOpen(false)} className="text-xs font-semibold text-primary hover:underline">
            See all notifications
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Breadcrumb, search, create and notifications */
export function TopBar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setOpen } = usePalette();
  const createActions = useCreateActions();
  const { setHeaderEl, actionsEl, setActionsEl, headerCount, recordLabel, panelCount, viewsHidden, setViewsHidden } = useShell();
  // Page buttons show only their icon when the top bar is narrow: each keeps its words as a tooltip
  useEffect(() => {
    if (!actionsEl) return;
    const name = () =>
      actionsEl.querySelectorAll("button").forEach((b) => {
        const text = b.textContent?.trim();
        if (text && !b.title) b.title = text;
        if (text && !b.getAttribute("aria-label")) b.setAttribute("aria-label", text);
      });
    name();
    const mo = new MutationObserver(name);
    mo.observe(actionsEl, { childList: true, subtree: true, characterData: true });
    return () => mo.disconnect();
  }, [actionsEl]);
  const base = getCrumbs(pathname);
  // A record page names itself in the last crumb
  const crumbs = recordLabel ? [...base.slice(0, -1), { ...base[base.length - 1]!, label: recordLabel }] : base;
  const pageHeader = headerCount > 0;

  return (
    // A container: what it shows follows its own width (smaller while Ask AI sits beside the page), not the window's
    <header className="@container/top sticky top-0 z-30 flex h-[52px] shrink-0 items-center gap-2 border-b border-border bg-card px-3 sm:px-5">
      <Button variant="ghost" size="icon-sm" className="md:hidden" aria-label="Open menu" onClick={onOpenMenu}>
        <Menu className="size-5" />
      </Button>
      {panelCount > 0 && (
        <Button variant="ghost" size="icon-sm" className="hidden md:inline-flex" aria-label={viewsHidden ? "Show views" : "Hide views"} aria-pressed={!viewsHidden} title={viewsHidden ? "Show views" : "Hide views"} onClick={() => setViewsHidden(!viewsHidden)}>
          <PanelLeft className="size-4" />
        </Button>
      )}

      {/* The page's own title and view switch (see ShellHeader) */}
      <div ref={setHeaderEl} className={pageHeader ? "flex min-w-0 flex-1 items-center" : "hidden"} />

      <Breadcrumb className={pageHeader ? "hidden" : "min-w-0 flex-1"}>
        <BreadcrumbList className="flex-nowrap text-[13px]">
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <React.Fragment key={`${c.label}-${i}`}>
                {i > 0 && <BreadcrumbSeparator className="hidden sm:flex" />}
                <BreadcrumbItem className={last ? "min-w-0" : "hidden sm:inline-flex"}>
                  {last ? (
                    <BreadcrumbPage className="truncate font-semibold">{c.label}</BreadcrumbPage>
                  ) : c.to ? (
                    <BreadcrumbLink asChild>
                      <Link to={c.to}>{c.label}</Link>
                    </BreadcrumbLink>
                  ) : (
                    <span>{c.label}</span>
                  )}
                </BreadcrumbItem>
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden h-8 min-w-[220px] items-center gap-2 rounded-lg border border-border bg-panel px-2.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted @5xl/top:flex"
      >
        <Search className="size-3.5" />
        <span className="flex-1">Search or run a command</span>
        <kbd className="rounded border border-border bg-card px-1.5 font-mono text-[10px]">Ctrl K</kbd>
      </button>
      <Button variant="ghost" size="icon-sm" className="@5xl/top:hidden" aria-label="Search" title="Search (Ctrl K)" onClick={() => setOpen(true)}>
        <Search className="size-4" />
      </Button>

      <AskButton />

      <ContactedChip />

      <CallChip />

      <RecentMenu />

      <Notifications />

      {/* The page's main actions (see ShellHeader) replace the global Create menu */}
      {/* Narrow: every button but the last (the main one, like New lead) shows only its icon */}
      <div
        ref={setActionsEl}
        className={
          pageHeader
            ? "flex shrink-0 items-center gap-2 @max-7xl/top:gap-1.5 @max-7xl/top:[&>button:not(:last-child):has(svg)]:gap-0 @max-7xl/top:[&>button:not(:last-child):has(svg)]:px-2 @max-7xl/top:[&>button:not(:last-child):has(svg)]:text-[0px] @max-7xl/top:[&>button:not(:last-child):has(svg)_svg]:m-0"
            : "hidden"
        }
      />

      {createActions.length > 0 && !pageHeader && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="h-8 gap-1.5">
              <Plus className="size-4" />
              <span className="hidden sm:inline">Create</span>
              <ChevronDown className="hidden size-3.5 opacity-70 sm:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {createActions.map((a) => (
              <DropdownMenuItem key={a.key} onClick={() => navigate(a.to)} className="cursor-pointer">
                <a.icon className="mr-2 size-4" />
                {a.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  );
}

/** Opens and closes the Ask AI panel (when the AI and the feature are on) */
function AskButton() {
  const { available, open, toggleAsk } = useAskPanel();
  if (!available) return null;
  return (
    <Button variant="outline" size="sm" className={cn("h-8 gap-1.5 px-2.5 text-xs", open && "border-primary/50 bg-accent")} aria-pressed={open} onClick={toggleAsk} title="Ask the CRM a question in everyday words (Ctrl J)">
      <AiIcon className="size-4" />
      <span className="hidden @4xl/top:inline">Ask AI</span>
    </Button>
  );
}
